<template>
  <div class="pi-aru">
    <div class="pi-aru__head">
      <span v-if="view.header.asOfText" class="pi-aru__asof">{{ view.header.asOfText }}</span>
      <span class="pi-badge" :class="'pi-badge--' + view.maturity.tone">{{ view.maturity.badgeText }}</span>
      <pi-definition-tooltip info-key="features.report.maturity" :label="view.maturity.badgeText"/>
      <span v-if="view.maturity.detail" class="pi-muted pi-aru__since">{{ view.maturity.detail }}</span>
    </div>
    <div v-if="view.maturity.text" class="pi-aru__notice" role="status">
      <p class="pi-aru__notice-text">{{ view.maturity.text }}</p>
    </div>

    <template v-if="view.maturity.measured">
      <p v-if="metaText" class="pi-muted pi-aru__meta">{{ metaText }}</p>
      <p v-if="view.header.visitText" class="pi-muted pi-aru__meta">{{ view.header.visitText }}</p>
      <p v-if="view.header.cutText" class="pi-notice">{{ view.header.cutText }}</p>
      <p v-if="view.header.periodEmptyText" class="pi-notice">{{ view.header.periodEmptyText }}</p>

      <div class="pi-cards pi-aru__cards">
        <pi-kpi-card v-for="card in view.cards" :key="card.key" :label="card.label" :metric="card.metric"
                     :definition-key="card.definitionKey" :info-key="card.infoKey" :definition-version="definitionVersion" :compare="false">
          <p v-for="line in card.lines" :key="line" class="pi-aru__card-line">{{ line }}</p>
        </pi-kpi-card>
      </div>

      <div class="row">
        <div class="col-xs-12 col-md-6">
          <div class="pi-aru__title-row">
            <h3 class="pi-subtitle pi-aru__title">Funil da visita</h3>
            <pi-definition-tooltip info-key="features.report.funnelStep" label="Funil da visita"/>
            <pi-export-menu title="Funil da visita" :screen="exportScreen" :columns="funnelColumns" :rows="view.funnel" :envelope="envelope"
                            :filters="tableFilters" :period="exportPeriod" :disabled="!view.funnel.length"/>
          </div>
          <pi-bar-list :rows="view.funnel" empty-text="Nenhuma visita ao relatório avançado no período."/>
          <p class="pi-muted pi-aru__note">{{ view.funnelNote }}</p>
        </div>
        <div class="col-xs-12 col-md-6">
          <div class="pi-aru__title-row">
            <h3 class="pi-subtitle pi-aru__title">Mudou a configuração x não mudou</h3>
            <pi-export-menu title="Mudou a configuração" :screen="exportScreen" :columns="changeColumns" :rows="view.change" :envelope="envelope"
                            :filters="tableFilters" :period="exportPeriod" :disabled="!view.change.length"/>
          </div>
          <div class="pi-table-wrap">
            <table class="pi-table">
              <caption class="pi-sr">Resultado e retorno das visitas que mudaram a configuração e das que não mudaram</caption>
              <thead>
              <tr>
                <pi-th v-for="column in changeHeader" :key="column.key" :column="column"/>
              </tr>
              </thead>
              <tbody>
              <tr v-for="row in view.change" :key="row.key">
                <th scope="row" class="pi-aru__row-head">
                  {{ row.label }}
                  <span class="pi-table__sub">{{ row.hint }}</span>
                </th>
                <td class="is-num">{{ row.visitsText }}</td>
                <td class="is-num"><pi-metric-cell :cell="row.resultRate"/></td>
                <td class="is-num"><pi-metric-cell :cell="row.returnRate"/></td>
              </tr>
              </tbody>
            </table>
          </div>
          <p v-if="view.changeSummary" class="pi-aru__finding">{{ view.changeSummary }}</p>
          <p class="pi-muted pi-aru__note">{{ view.changeNote }}</p>

          <div class="pi-aru__title-row pi-aru__sub">
            <h3 class="pi-subtitle pi-aru__title">Profundidade da visita</h3>
            <pi-export-menu title="Profundidade da visita" :screen="exportScreen" :columns="depthColumns" :rows="view.depth" :envelope="envelope"
                            :filters="tableFilters" :period="exportPeriod" :disabled="!view.depth.length"/>
          </div>
          <div class="pi-table-wrap">
            <table class="pi-table">
              <caption class="pi-sr">Percentis de configurações, execuções e minutos por visita</caption>
              <thead>
              <tr>
                <pi-th v-for="column in depthHeader" :key="column.key" :column="column"/>
              </tr>
              </thead>
              <tbody>
              <tr v-for="row in view.depth" :key="row.key">
                <th scope="row" class="pi-aru__row-head">{{ row.label }}</th>
                <td class="is-num">{{ row.p50Text }}</td>
                <td class="is-num">{{ row.p75Text }}</td>
                <td class="is-num">{{ row.p90Text }}</td>
              </tr>
              </tbody>
            </table>
          </div>
          <p class="pi-muted pi-aru__note">{{ view.depthNote }}</p>
        </div>
      </div>

      <div class="pi-aru__title-row pi-aru__sub">
        <h3 class="pi-subtitle pi-aru__title">Quem tira valor e quem se confunde</h3>
        <pi-definition-tooltip info-key="features.report.segment" label="Segmentos de uso" :version="definitionVersion"/>
        <pi-export-menu title="Segmentos de uso" :screen="exportScreen" :columns="segmentColumns" :rows="view.segments" :envelope="envelope"
                        :filters="tableFilters" :period="exportPeriod" :disabled="!view.segments.length"/>
      </div>
      <p class="pi-muted pi-aru__meta">Cada usuário cai no primeiro segmento que casa, nesta ordem. Clique no segmento para ver só os usuários dele na lista.</p>
      <div v-if="view.segments.length" class="pi-table-wrap">
        <table class="pi-table">
          <caption class="pi-sr">Usuários por segmento de uso nos 30 dias</caption>
          <thead>
          <tr>
            <pi-th v-for="column in segmentHeader" :key="column.key" :column="column"/>
          </tr>
          </thead>
          <tbody>
          <tr v-for="segment in view.segments" :key="segment.key" :class="{ 'pi-row-selected': segment.selected }">
            <th scope="row" class="pi-aru__row-head">
              <button v-if="segment.selectable" type="button" class="pi-aru__segment" :aria-pressed="segment.selected ? 'true' : 'false'"
                      @click="$emit('segment', segment.key)">{{ segment.label }}</button>
              <span v-else>{{ segment.label }}</span>
              <span v-if="segment.definition" class="pi-table__sub">{{ segment.definition }}</span>
            </th>
            <td><span class="pi-badge" :class="'pi-badge--' + segment.tone">{{ segment.groupLabel }}</span></td>
            <td class="is-num">{{ segment.usersText }}</td>
            <td class="is-num">{{ segment.companiesText }}</td>
            <td class="is-num"><pi-metric-cell :cell="segment.share" :show-reason="false"/></td>
          </tr>
          </tbody>
        </table>
      </div>
      <p v-else class="pi-muted">Sem segmentos nesta janela.</p>

      <div class="pi-aru__title-row pi-aru__sub">
        <h3 class="pi-subtitle pi-aru__title">Combinações que dão resultado</h3>
        <pi-definition-tooltip info-key="features.report.combination" label="Combinações que dão resultado" :version="definitionVersion"/>
        <pi-export-menu title="Combinações" :screen="exportScreen" :columns="combinationColumns" :rows="combinationRows" :envelope="envelope"
                        :filters="tableFilters" :period="exportPeriod" :disabled="!combinationRows.length"/>
      </div>
      <p class="pi-muted pi-aru__meta">{{ view.combinationNote }}</p>
      <div v-if="view.combinations.length || view.otherCombination" class="pi-table-wrap">
        <table class="pi-table">
          <caption class="pi-sr">Combinações de relatório, modo e agrupamento com resultado e retorno</caption>
          <thead>
          <tr>
            <pi-th v-for="column in combinationHeader" :key="column.key" :column="column"/>
          </tr>
          </thead>
          <tbody>
          <tr v-for="row in view.combinations" :key="row.key">
            <td>
              {{ row.recordText }}
              <span v-if="row.companiesText" class="pi-table__sub">{{ row.companiesText }}</span>
            </td>
            <td>{{ row.modeText }}</td>
            <td>{{ row.groupByText }}</td>
            <td class="is-num">{{ row.usersText }}</td>
            <td class="is-num">{{ row.visitsRanText }}</td>
            <td class="is-num">{{ row.visitsResultText }}</td>
            <td class="is-num"><pi-metric-cell :cell="row.resultRate"/></td>
            <td class="is-num"><pi-metric-cell :cell="row.returnRate" ratio-suffix="usuários"/></td>
          </tr>
          <tr v-if="view.otherCombination" class="pi-aru__other">
            <td colspan="3">
              {{ view.otherCombination.label }}
              <span class="pi-table__sub">{{ view.otherCombination.hint }}</span>
            </td>
            <td class="is-num">{{ view.otherCombination.usersText }}</td>
            <td class="is-num">{{ view.otherCombination.visitsRanText }}</td>
            <td class="is-num">{{ view.otherCombination.visitsResultText }}</td>
            <td class="is-num"><pi-metric-cell :cell="view.otherCombination.resultRate"/></td>
            <td class="is-num">—</td>
          </tr>
          </tbody>
        </table>
      </div>
      <p v-else class="pi-muted">Nenhuma combinação executada no período.</p>

      <div class="pi-aru__title-row pi-aru__sub">
        <h3 class="pi-subtitle pi-aru__title">Onde trava</h3>
        <pi-export-menu title="Onde trava" :screen="exportScreen" :columns="failureColumns" :rows="view.failures" :envelope="envelope"
                        :filters="tableFilters" :period="exportPeriod" :disabled="!view.failures.length"/>
      </div>
      <div v-if="view.failures.length" class="pi-table-wrap">
        <table class="pi-table">
          <caption class="pi-sr">Recusas e erros por código, ação e relatório</caption>
          <thead>
          <tr>
            <pi-th v-for="column in failureHeader" :key="column.key" :column="column"/>
          </tr>
          </thead>
          <tbody>
          <tr v-for="row in view.failures" :key="row.key">
            <td>
              {{ row.label }}
              <span v-if="row.technical" class="pi-badge pi-badge--bad">erro técnico</span>
              <span v-if="row.code && row.code !== row.label" class="pi-table__sub">{{ row.code }}</span>
            </td>
            <td>{{ row.kindText }}</td>
            <td>{{ row.recordText }}</td>
            <td class="is-num">{{ row.eventsText }}</td>
            <td class="is-num">{{ row.usersText }}</td>
            <td class="is-num">{{ row.companiesText }}</td>
            <td class="is-num">{{ row.endedText }}</td>
          </tr>
          </tbody>
        </table>
      </div>
      <p v-else class="pi-muted">Nenhuma recusa nem erro no período.</p>
      <p class="pi-muted pi-aru__note">{{ view.technical.text }}</p>

      <div class="pi-aru__title-row pi-aru__minor-row">
        <h4 class="pi-aru__minor">Execuções vazias</h4>
        <pi-export-menu title="Execuções vazias" :screen="exportScreen" :columns="emptyColumns" :rows="view.emptyRuns" :envelope="envelope"
                        :filters="tableFilters" :period="exportPeriod" :disabled="!view.emptyRuns.length"/>
      </div>
      <div v-if="view.emptyRuns.length" class="pi-table-wrap">
        <table class="pi-table">
          <caption class="pi-sr">Execuções sem linhas por relatório e modo</caption>
          <thead>
          <tr>
            <pi-th v-for="column in emptyHeader" :key="column.key" :column="column"/>
          </tr>
          </thead>
          <tbody>
          <tr v-for="row in view.emptyRuns" :key="row.key">
            <td>{{ row.recordText }}</td>
            <td>{{ row.modeText }}</td>
            <td class="is-num">{{ row.runsText }}</td>
            <td class="is-num">{{ row.emptyRunsText }}</td>
            <td class="is-num"><pi-metric-cell :cell="row.emptyRate" :show-ratio="false"/></td>
            <td class="is-num">{{ row.usersText }}</td>
          </tr>
          </tbody>
        </table>
      </div>
      <p v-else class="pi-muted">Nenhum relatório com 5 execuções ou mais no período.</p>
      <p class="pi-muted pi-aru__note">{{ view.emptyRunsNote }}</p>

      <div class="pi-aru__title-row pi-aru__sub">
        <h3 class="pi-subtitle pi-aru__title">Empresas</h3>
        <pi-export-menu title="Empresas no relatório avançado" :screen="exportScreen" :columns="companyColumns" :rows="view.companies"
                        :envelope="envelope" :filters="tableFilters" :period="exportPeriod" :disabled="!view.companies.length"/>
      </div>
      <p class="pi-muted pi-aru__meta">Até 50 empresas com mais usuários na tela, nos 30 dias.</p>
      <div v-if="view.companies.length" class="pi-table-wrap">
        <table class="pi-table">
          <caption class="pi-sr">Empresas com mais usuários no relatório avançado</caption>
          <thead>
          <tr>
            <pi-th v-for="column in companyHeader" :key="column.key" :column="column"/>
          </tr>
          </thead>
          <tbody>
          <tr v-for="company in view.companies" :key="company.key">
            <td><pi-company-name :company-id="company.companyId" :shard-code="company.shardCode" :name="company.name" :query="view.query" stacked/></td>
            <td class="is-num">{{ company.usersText }}</td>
            <td class="is-num">{{ company.valueUsersText }}</td>
            <td class="is-num">{{ company.confusedUsersText }}</td>
            <td class="is-num">{{ company.visitsText }}</td>
            <td class="is-num">{{ company.resultsText }}</td>
          </tr>
          </tbody>
        </table>
      </div>
      <p v-else class="pi-muted">Nenhuma empresa nos 30 dias.</p>

      <div class="pi-aru__title-row pi-aru__sub">
        <h3 class="pi-subtitle pi-aru__title">Usuários</h3>
        <span v-if="view.stageText" class="pi-aru__stage">
          {{ view.stageText }}
          <button type="button" class="pi-btn pi-btn--ghost pi-aru__clear" @click="$emit('segment', view.stage)">Ver todos</button>
        </span>
        <pi-export-menu title="Usuários do relatório avançado" :screen="exportScreen" :columns="userColumns" :source="usersSource"
                        :filters="userFilters" :period="exportPeriod" :disabled="!usersSource || loading || !view.paging.total"/>
      </div>
      <p class="pi-muted pi-aru__meta">{{ view.usersNote }}</p>
      <framed-table v-if="view.users.length || view.paging.total" :striped="false">
        <caption class="pi-sr">Usuários do relatório avançado nos 30 dias</caption>
        <thead>
        <tr>
          <template v-for="column in columns">
            <pi-sort-th v-if="column.sortable" :key="column.key" :column="column" :state="sortState" :disabled="loading" @sort="$emit('sort', $event)"/>
            <pi-th v-else :key="column.key" :column="column"/>
          </template>
        </tr>
        </thead>
        <tbody>
        <tr v-for="row in view.users" :key="row.key">
          <td><pi-company-name :company-id="row.companyId" :shard-code="row.shardCode" :name="row.companyName" :query="view.query" stacked/></td>
          <td class="pi-aru__user">{{ row.userText }}</td>
          <td>{{ row.roleText }}</td>
          <td><span class="pi-badge" :class="'pi-badge--' + row.tone">{{ row.segmentText }}</span></td>
          <td class="is-num">{{ row.visitsText }}</td>
          <td class="is-num">{{ row.attemptsText }}</td>
          <td class="is-num">{{ row.goodDaysText }}</td>
          <td class="is-num">
            {{ row.configsText }}
            <span v-if="row.resultConfigsText" class="pi-table__sub">{{ row.resultConfigsText }}</span>
          </td>
          <td class="is-num">{{ row.resultsText }}</td>
          <td class="is-num">{{ row.badShareText }}</td>
          <td>{{ row.lastVisitText }}</td>
          <td>{{ row.topFailureText }}</td>
        </tr>
        </tbody>
        <table-pager slot="footer" :total="view.paging.total" :page="view.paging.page" :page-size="view.paging.pageSize"
                     @update:page="$emit('page', $event)" @update:pageSize="$emit('page-size', $event)"/>
      </framed-table>
      <p v-else class="pi-muted">{{ view.stage ? 'Nenhum usuário neste segmento nos 30 dias.' : 'Nenhum usuário nos 30 dias.' }}</p>
    </template>

    <div class="pi-aru__limits">
      <h3 class="pi-subtitle">Limitações</h3>
      <ul v-if="view.limitations.length" class="pi-aru__limit-list">
        <li v-for="(item, index) in view.limitations" :key="index">{{ item }}</li>
      </ul>
      <p v-else class="pi-muted">Nenhuma limitação informada.</p>
    </div>
  </div>
</template>

<script>
import framedTable from "../default/framedTable.vue";
import tablePager from "../default/tablePager.vue";
import piKpiCard from "./piKpiCard.vue";
import piBarList from "./piBarList.vue";
import piMetricCell from "./piMetricCell.vue";
import piSortTh from "./piSortTh.vue";
import piTh from "./piTh.vue";
import piCompanyName from "./piCompanyName.vue";
import piDefinitionTooltip from "./piDefinitionTooltip.vue";
import piExportMenu from "./piExportMenu.vue";
import { screenColumns } from "./piColumns.js";
import {
  REPORT_CHANGE_COLUMNS, REPORT_COMBINATION_COLUMNS, REPORT_COMPANY_COLUMNS, REPORT_DEPTH_COLUMNS, REPORT_EMPTY_COLUMNS, REPORT_FAILURE_COLUMNS,
  REPORT_FUNNEL_COLUMNS, REPORT_SEGMENT_COLUMNS, USER_COLUMNS, combinationExportRows, usageLines, userListLines,
} from "./piAdvancedReportModel.js";

export default {
  name: "pi-advanced-report-usage",
  components: { framedTable, tablePager, piKpiCard, piBarList, piMetricCell, piSortTh, piTh, piCompanyName, piDefinitionTooltip, piExportMenu },
  props: {
    view: { type: Object, required: true },
    sortState: { type: Object, default: null },
    loading: { type: Boolean, default: false },
    definitionVersion: { type: Number, default: null },
    envelope: { type: Object, default: null },
    exportScreen: { type: String, default: "Funcionalidade" },
    exportFilters: { type: Array, default: () => [] },
    exportPeriod: { type: Object, default: null },
    usersSource: { type: Object, default: null },
  },
  data() {
    return {
      columns: screenColumns(USER_COLUMNS),
      userColumns: USER_COLUMNS,
      funnelColumns: REPORT_FUNNEL_COLUMNS,
      changeColumns: REPORT_CHANGE_COLUMNS,
      changeHeader: screenColumns(REPORT_CHANGE_COLUMNS),
      depthColumns: REPORT_DEPTH_COLUMNS,
      depthHeader: screenColumns(REPORT_DEPTH_COLUMNS),
      segmentColumns: REPORT_SEGMENT_COLUMNS,
      segmentHeader: screenColumns(REPORT_SEGMENT_COLUMNS),
      combinationColumns: REPORT_COMBINATION_COLUMNS,
      combinationHeader: screenColumns(REPORT_COMBINATION_COLUMNS),
      failureColumns: REPORT_FAILURE_COLUMNS,
      failureHeader: screenColumns(REPORT_FAILURE_COLUMNS),
      emptyColumns: REPORT_EMPTY_COLUMNS,
      emptyHeader: screenColumns(REPORT_EMPTY_COLUMNS),
      companyColumns: REPORT_COMPANY_COLUMNS,
      companyHeader: screenColumns(REPORT_COMPANY_COLUMNS),
    };
  },
  computed: {
    metaText() {
      return [this.view.header.populationText, this.view.header.windowText].filter(Boolean).join(" · ");
    },
    combinationRows() {
      return combinationExportRows(this.view);
    },
    tableFilters() {
      return this.exportFilters.concat(usageLines(this.view));
    },
    userFilters() {
      return this.exportFilters.concat(userListLines(this.view, this.sortState));
    },
  },
};
</script>

<style src="./pi.css"></style>

<style scoped>
.pi-aru__head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 10px;
  margin-bottom: 8px;
}

.pi-aru__asof {
  font-size: 12px;
  font-weight: 700;
  color: #3a424e;
}

.pi-aru__notice {
  margin: 0 0 12px;
  padding: 10px 14px;
  border: 1px dashed #c5cad3;
  border-radius: 6px;
  background: #fafbfc;
}

.pi-aru__notice-text {
  margin: 0;
  font-size: 12px;
  color: #3a424e;
}

.pi-aru__meta {
  margin-bottom: 6px;
}

.pi-aru__cards {
  margin-top: 10px;
}

.pi-aru__card-line {
  margin: 0;
  font-size: 11px;
  color: #6b7280;
}

.pi-aru__note {
  margin-top: 6px;
}

.pi-aru__sub {
  margin-top: 20px;
}

.pi-aru__row-head {
  font-weight: 600;
  color: #1f2933;
}

.pi-aru__finding {
  margin: 8px 0 0;
  padding: 6px 10px;
  border-left: 3px solid #c5cad3;
  background: #fafbfc;
  font-size: 12px;
  color: #3a424e;
}

.pi-aru__title-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 10px;
  margin-bottom: 8px;
}

.pi-aru__title {
  margin: 0;
}

.pi-aru__segment {
  padding: 0;
  border: 0;
  background: none;
  color: #1b59f8;
  font: inherit;
  font-weight: 700;
  text-align: left;
  cursor: pointer;
}

.pi-aru__segment:hover,
.pi-aru__segment:focus {
  text-decoration: underline;
}

.pi-aru__other > td {
  color: #4e5560;
  background: #fafbfc;
}

.pi-aru__minor-row {
  margin: 16px 0 6px;
}

.pi-aru__minor-row .pi-aru__minor {
  margin: 0;
}

.pi-aru__minor {
  margin: 16px 0 6px;
  font-size: 12px;
  font-weight: 700;
  color: #3a424e;
}

.pi-aru__stage {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #1340b5;
}

.pi-aru__clear {
  height: 26px;
  padding: 0 10px;
  font-size: 11px;
}

.pi-aru__user {
  font-family: Consolas, "Courier New", monospace;
  white-space: nowrap;
}

.pi-aru__limits {
  margin-top: 20px;
}

.pi-aru__limit-list {
  margin: 0;
  padding-left: 18px;
  font-size: 12px;
  color: #4e5560;
}
</style>



// WEBPACK FOOTER //
// src/vue/pages/components/pi/piAdvancedReportUsage.vue